import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { toast, Loader } from '@grandvisionhq/common';
import { useLink } from '@grandvisionhq/frk-routing';
import { useIsClient } from '@grandvisionhq/lib-utils-react';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useCart } from '../../context/Cart/context.js';
import '@swc/helpers';
import '@grandvisionhq/graphql';
import '@grandvisionhq/svc-tealium/events';
import '@grandvisionhq/utils';
import { useCartSettings } from '../../context/CartSettings/context.js';
import '../../models/add-to-cart/add-to-cart.js';
import '../../models/cart/cart.js';
import '../../models/checkoutState/checkoutState.js';
import '../../utils/cart-count-var.js';
import '../../utils/cart-id-var.js';
import '@grandvisionhq/commercetools';
import { validationErrorLabelMap } from '../../utils/get-validation-error-codes.js';
import '../../utils/get-unrecoverable-validation-errors.js';
import '../../models/line-item/prescription-glasses.js';
import '../../utils/get-failure-error-codes.js';
import '../../utils/get-validation-errors-by-line-item.js';
import '@grandvisionhq/lib-visuals';
import '../../operations/fragments/cart-details.js';
import '../../operations/fragments/cart-validation-errors.js';
import '../../operations/fragments/recover-inactive-cart-failure.js';
import '../../operations/fragments/recover-inactive-cart-result.js';
import '../../operations/mutations/add-cart-line-items.js';
import '../../operations/mutations/add-cart-voucher-codes.js';
import '../../operations/mutations/create-cart.js';
import '../../operations/mutations/create-cart-from-instant-checkout-link.js';
import '../../operations/mutations/create-cart-from-order.js';
import '../../operations/mutations/duplicate-cart-line-items.js';
import '../../operations/mutations/remove-cart-voucher-codes.js';
import '../../operations/mutations/remove-cart-line-items.js';
import '../../operations/mutations/update-cart-line-items.js';
import '../../operations/mutations/recover-inactive-cart.js';
import '../../operations/queries/get-cart.js';
import '../../operations/queries/get-total-line-items.js';
import '../../operations/queries/get-cart-estimated-delivery.js';
import { mapLineItemToDataLayerEvent } from '../../mappers/analytics/data-layer/line-item-to-data-layer-event.js';
import '@grandvisionhq/payments';
import '../../mappers/analytics/tealium/line-item-to-tealium-product.js';
import { EmptyCartContainer } from '../EmptyCart/containers/EmptyCartContainer.js';
import { Cart as Cart$1 } from './components/Cart.js';

const Cart = ({ policyItems , donation  })=>{
    const { isDynamicLineItemDeliveryEstimationEnabled  } = useFeatureToggles();
    const { cart , loading , error  } = useCart();
    const settings = useCartSettings();
    const { getLabel , hasLabel  } = useIntl();
    const cartExists = Boolean(cart);
    const isClient = useIsClient();
    const { onClick: redirectToHome  } = useLink({
        route: 'home'
    }) || {};
    const hasDistinctDeliveryEstimationsAlert = Boolean(isDynamicLineItemDeliveryEstimationEnabled && hasLabel('cart.alert.distinctDeliveryEstimations.title') && (cart === null || cart === void 0 ? void 0 : cart.hasDistinctDeliveryEstimations));
    const labels = {
        cartTitle: getLabel('cart.title'),
        alertDistinctDeliveryEstimationsTitle: getLabel('cart.alert.distinctDeliveryEstimations.title'),
        alertDistinctDeliveryEstimationsDescription: hasLabel('cart.alert.distinctDeliveryEstimations.description') ? getLabel('cart.alert.distinctDeliveryEstimations.description') : undefined,
        unavailableTitle: getLabel('cart.unavailableItems.title')
    };
    React.useEffect(()=>{
        if (cart === null || cart === void 0 ? void 0 : cart.validationErrors) {
            const errorCodes1 = Object.entries(cart.validationErrors).flatMap(([, errorCodes])=>{
                return errorCodes.map((code)=>validationErrorLabelMap[code]
                );
            });
            new Set(errorCodes1).forEach((code)=>{
                toast.failed(`cart.${code}`);
            });
        }
    }, [
        cart === null || cart === void 0 ? void 0 : cart.validationErrors
    ]);
    const handleCheckoutNavigation = React.useCallback(()=>{
        if (!(cart === null || cart === void 0 ? void 0 : cart.id) || !(cart === null || cart === void 0 ? void 0 : cart.lineItems.length)) return;
        const dataLayerEvents = mapLineItemToDataLayerEvent(DataLayer.DataLayerTypes.EventType.checkout, (cart === null || cart === void 0 ? void 0 : cart.lineItems) || [], (cart === null || cart === void 0 ? void 0 : cart.id) || '');
        dataLayerEvents && DataLayer.pushEcommerceEvent(dataLayerEvents);
    }, [
        cart === null || cart === void 0 ? void 0 : cart.id,
        cart === null || cart === void 0 ? void 0 : cart.lineItems
    ]);
    if (!isClient) {
        return null;
    }
    if (loading) {
        return /*#__PURE__*/ React.createElement("div", {
            className: "cart",
            "data-t": "loading-cart"
        }, /*#__PURE__*/ React.createElement(Loader, null));
    }
    const lineItems = (cart === null || cart === void 0 ? void 0 : cart.lineItems.filter((lineItem)=>{
        var ref;
        return !(cart === null || cart === void 0 ? void 0 : (ref = cart.validationErrors) === null || ref === void 0 ? void 0 : ref[lineItem.id]);
    })) || [];
    const unavailableLineItems = (cart === null || cart === void 0 ? void 0 : cart.lineItems.filter((lineItem)=>{
        var ref;
        return cart === null || cart === void 0 ? void 0 : (ref = cart.validationErrors) === null || ref === void 0 ? void 0 : ref[lineItem.id];
    })) || [];
    const cartEmpty = lineItems.length < 1 && unavailableLineItems.length < 1;
    const deliveryResolver = settings.deliveryResolverFactory && settings.deliveryResolverFactory(lineItems);
    const visualLineItems = (cart === null || cart === void 0 ? void 0 : cart.visualLineItems) || [];
    const availableVisualLineItems = visualLineItems.filter((visualLineItem)=>{
        return lineItems.some((lineItem)=>lineItem.id === visualLineItem.id
        );
    });
    if (!cartExists || error || cartEmpty) {
        return /*#__PURE__*/ React.createElement(EmptyCartContainer, {
            onClick: ()=>{
                return redirectToHome === null || redirectToHome === void 0 ? void 0 : redirectToHome();
            }
        });
    }
    return cart ? /*#__PURE__*/ React.createElement(Cart$1, {
        cartId: cart.id,
        deliveryResolver: deliveryResolver,
        donation: donation,
        handleCheckoutNavigation: handleCheckoutNavigation,
        hasSubscription: Boolean(cart === null || cart === void 0 ? void 0 : cart.hasSubscription),
        isReadOnly: settings.isReadOnly,
        labels: labels,
        lineItems: lineItems,
        policyItems: policyItems,
        hasDistinctDeliveryEstimationsAlert: hasDistinctDeliveryEstimationsAlert,
        unavailableLineItems: unavailableLineItems,
        visualLineItems: availableVisualLineItems
    }) : null;
};

export { Cart };
//# sourceMappingURL=Cart.js.map
